"use client";

import { useState, useEffect } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { usePathname } from "next/navigation";

import { cn, useTina } from "@/lib/utils";
import { Logo } from "@/assets";

import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";

export default function Navbar({ globalData }) {
  const pathname = usePathname();
  // const [isOpen, setIsOpen] = useState(false);

  const { data } = useTina({
    query: globalData.query,
    variables: globalData.variables,
    data: globalData.data,
  });

  const { navItems } = data.global;

  // Create a stable isActive function that will be consistent between server and client
  const isLinkActive = (href) => {
    if (href === "/") {
      return pathname === "" || pathname === "/";
    }
    return pathname === href;
  };

  const [hidden, setHidden] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { scrollY } = useScroll();

  const update = () => {
    if (scrollY?.current < scrollY?.prev) {
      setHidden(false);
    } else if (scrollY?.current > 220 && scrollY?.current > scrollY?.prev) {
      setHidden(true);
    }
  };

  useMotionValueEvent(scrollY, "change", () => {
    update();
  });

  return (
    <>
      <motion.nav
        className="py-6 hidden xl:block border-b sticky top-0 z-50 bg-background-blackish border-white/10"
        animate={hidden ? "hidden" : "visible"}
        variants={{ hidden: { y: -180 }, visible: { y: 0 } }}
        transition={{ ease: "easeInOut", duration: 0.5 }}
      >
        <div className="flex justify-between items-center content">
          <div className="flex items-center gap-12">
            <div>
              <Link href="/" aria-label="Home">
                <Logo className="h-10 text-primary-500" />
              </Link>
            </div>
            <div className="flex items-center gap-8">
              {navItems?.navLinks?.map((link) => {
                const isActive = isLinkActive(link.link);

                if (!link.link && !link.label) return null;

                return (
                  <Link
                    key={link.link}
                    href={link?.link || "/"}
                    className={cn(
                      "relative transition-colors font-medium hover:text-primary-300",
                      isActive && "text-primary-300"
                    )}
                  >
                    {link.label}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-underline"
                        className="absolute -bottom-[0.5rem] left-0 right-0 h-0.5 bg-primary-300"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        style={{ originY: "0px" }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end items-center gap-6">
            {navItems?.navButtons?.map((action, index) => (
              <Button
                key={index}
                asChild
                variant={index === 0 ? "link-primary" : "default"}
                size="sm"
                trackEvent={action?.label}
                trackProps={{
                  page: "Global",
                  section: "Nav",
                }}
              >
                <Link href={action?.link || "#"}>{action?.label}</Link>
              </Button>
            ))}
          </div>
        </div>
      </motion.nav>
      <motion.nav
        className="xl:hidden py-4 shadow-sm  sticky top-0 z-50 bg-background-blackish border-b border-white/10"
        animate={hidden ? "hidden" : "visible"}
        variants={{ hidden: { y: -180 }, visible: { y: 0 } }}
        transition={{ ease: "easeInOut", duration: 0.5 }}
      >
        <div className="flex justify-between items-center content">
          <Link href="/" aria-label="Home">
            <Logo className="h-8 text-primary-500" />
          </Link>
          <div className="flex items-center gap-4">
            <MobileMenu
              // buttons={callToAction}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              navItems={navItems?.navLinks}
              buttons={navItems?.navButtons}
              isLinkActive={isLinkActive}
            />
          </div>
        </div>
      </motion.nav>
    </>
  );
}

const menu = {
  show: {
    clipPath: "ellipse(200% 150% at 100% 0%)",
    transition: {
      type: "spring",
      stiffness: 40,
      restDelta: 2,
      // delay: 0.2,
      delayChildren: 0.2,
      staggerChildren: 0.05,
    },
  },
  hidden: {
    clipPath: "ellipse(50% 25% at 120% -30%)",
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
    },
  },
};

const linkVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      // duration: 0.04,
      // ease: "easeInOut",
      type: "spring",
      stiffness: 75,
      mass: 0.5,
    },
  },
};
function MobileMenu({ navItems, buttons, isOpen, setIsOpen, isLinkActive }) {
  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add("prevent-scroll");
      // window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.documentElement.classList.remove("prevent-scroll");
    }
    return () => {};
  }, [isOpen]);

  const pathname = usePathname();

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
      document.documentElement.classList.remove("prevent-scroll");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      <button
        onClick={handleClick}
        className="flex flex-col justify-center items-center"
        aria-label="Home"
      >
        <span
          className={`bg-primary-500 block transition-all duration-300 ease-out h-0.5 w-6 rounded-lg ${
            isOpen ? "rotate-45 translate-y-1" : "-translate-y-[3px]"
          }`}
        ></span>
        <span
          className={`bg-primary-500 block transition-all duration-300 ease-out h-0.5 w-6 rounded-lg my-0.5 ${
            isOpen ? "opacity-0" : "opacity-100"
          }`}
        ></span>
        <span
          className={`bg-primary-500 block transition-all duration-300 ease-out h-0.5 w-6 rounded-lg' ${
            isOpen ? "-rotate-45 -translate-y-1" : "translate-y-[3px]"
          }`}
        ></span>
      </button>
      <motion.div
        className={cn(
          "fixed left-0 z-50 visible w-full overflow-hidden px-6 pt-6 pb-6 mb-12 bg-gradient-to-b from-background-blackish via-background-purplish to-background-purplish flex flex-col justify-between items-center top-[56px] h-[calc(100svh-56px)]",
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
        initial="hidden"
        animate={isOpen ? "show" : "hidden"}
        variants={menu}
      >
        {navItems.map(({ link, label }, index) => {
          const isActive = isLinkActive(link);
          return (
            <motion.div key={index} variants={linkVariants}>
              <Link
                href={link || "/"}
                className={cn(
                  "text-lg text-white !font-normal",
                  isActive && "text-primary-300"
                )}
              >
                {label}
              </Link>
            </motion.div>
          );
        })}

        <div className="flex flex-col gap-4 w-full ">
          {buttons.map(({ link, label, onClick }, index) => (
            <motion.div
              key={index + "button"}
              variants={linkVariants}
              onClick={onClick}
            >
              <Button
                asChild
                className="w-full !py-4 h-12 !scale-100"
                trackEvent={label}
                trackProps={{
                  page: "Global",
                  section: "Nav",
                }}
                variant={index === buttons.length - 1 ? "default" : "outline"}
              >
                {link ? (
                  <Link href={link} aria-label={label}>
                    {label}
                  </Link>
                ) : (
                  <span>{label}</span>
                )}
              </Button>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </>
  );
}
